/* eslint-disable react-hooks/rules-of-hooks */
import styled from '@emotion/styled'
import Link, { LinkProps } from 'next/link'
import { Colors, Levels, Shapes, Typographies, useTypography } from 'src/components/theme'
import Logo from 'src/assets/images/icon.svg'
import { FlexColumn } from 'src/components/flex-containers'
import { Map } from '@emotion-icons/fa-solid/Map'
import { HouseUser } from '@emotion-icons/fa-solid/HouseUser'
import { MapMarkedAlt } from '@emotion-icons/fa-solid/MapMarkedAlt'
import { HelpOutline } from '@emotion-icons/material-rounded/HelpOutline'
import { TenantFinderIcon } from 'src/views/tenant-finder/icon'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { useMemo } from 'react'
import { APP_SECTIONS_ROUTES } from 'src/layout/utils/routing'
import dynamic from 'next/dynamic'
import { PaddedBox } from 'src/components/generic/padded-box'
import { CommandAILink } from 'src/layout/toolbar/links/command-ai'
import { NavMenuModalToogle } from 'src/components/account/menu'
import { ChatDotsFill } from '@emotion-icons/bootstrap/ChatDotsFill'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'
import { AtlasLink } from 'src/layout/toolbar/links/Atlas'
import { isFeatureEnabled } from 'src/utils/feature-flag'

const ToolbarAdmin = dynamic(() => import('src/layout/toolbar/admin'), {
  ssr: false,
})

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 80px;
  justify-content: space-between;
  background-color: ${Colors.neutral950};
  z-index: ${Levels.navigationComponents};
  padding-top: ${Shapes.spacing[2]}px;
`

const LinkItem = styled(Link)<{ active: 0 | 1 }>`
  width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${Shapes.spacing[4]}px;
  padding-inline: ${Shapes.spacing[2]}px;
  padding-block: ${Shapes.spacing[4]}px;
  color: ${Colors.bgWhite};
  background-color: ${(props) => (props.active ? Colors.primaryBlue900 : Colors.neutral950)};
  word-wrap: break-word;
  text-align: center;
  ${useTypography(Typographies.textmdm)}
  @media (min-height: 820px) {
    padding-block: 20px;
  }

  svg {
    height: 16px;
    width: auto;
    color: ${(props) => (props.active ? Colors.primaryBlue400 : Colors.neutral300)};
    transition: 200ms ease-in-out;
    transform: ${(props) => (props.active ? 'scale(1.2)' : 'scale(1)')};
  }

  :hover {
    background-color: ${Colors.neutral900};
    // scale up the icon a bit
    svg {
      color: ${Colors.textWhite};
      transform: scale(1.2);
    }
  }
`
const HomeItem = styled(LinkItem)`
  height: 56px;
  padding-block: 0;
`
const BottomSection = styled(FlexColumn)`
  flex: 0;
  border-top: 1px solid ${Colors.neutral900};
`

const MenuItem = (
  props: LinkProps & {
    id?: string
    children: React.ReactNode
    disableactive?: 0 | 1
  }
) => {
  const { id, href, disableactive } = props
  const { query, router } = useParsedRouter()

  // Based on the menu item's href, determine if it should be active by checking if its associated routes are in the current path
  const active = useMemo(() => {
    if (typeof href !== 'string') {
      return false
    }
    switch (href) {
      case '/':
        // Special case for the home page
        if (router.asPath === '/') {
          return true
        }
        return (
          APP_SECTIONS_ROUTES.mapExplorer.some((route) => router.asPath.includes(route)) &&
          !router.asPath.includes('/owner-reveal')
        )
      case '/reports':
        return APP_SECTIONS_ROUTES.reports.some((route) => router.asPath.includes(route))
      case '/tenant-finder':
        return APP_SECTIONS_ROUTES.tenantFinder.some((route) => router.asPath.includes(route))
      case '/map-builder':
        return APP_SECTIONS_ROUTES.mapBuilder.some((route) => router.asPath.includes(route))
      case '/owner-reveal':
        return APP_SECTIONS_ROUTES.ownerReveal.some((route) => router.asPath.includes(route))
      case '/help-center':
        return APP_SECTIONS_ROUTES.helpCenter.some((route) => router.asPath.includes(route))
      default:
        return false
    }
  }, [router.asPath])

  const redir =
    typeof href === 'string'
      ? {
          pathname: href,
          query,
        }
      : href

  return <LinkItem id={id} active={disableactive || !active ? 0 : 1} {...props} href={redir} />
}

export const VerticalToolbar = () => {
  const isOwnerRevealEnabled = isFeatureEnabled('owner-reveal')
  return (
    <Wrapper id={COMMANDAI_IDS_MAP.chromeBar['bar']}>
      <FlexColumn gap={Shapes.spacing[2]}>
        <HomeItem active={0} href={'/'} id={COMMANDAI_IDS_MAP.chromeBar['home']}>
          <img src={Logo || null} height={32} width="auto" alt="Alphamap logo" />
        </HomeItem>
        <MenuItem href={'/'} id={COMMANDAI_IDS_MAP.chromeBar['map-explorer']}>
          <Map />
          Map Explorer
        </MenuItem>
        <MenuItem href={'/tenant-finder'} id={COMMANDAI_IDS_MAP.chromeBar['tenant-finder']}>
          <TenantFinderIcon />
          Tenant Finder™
        </MenuItem>
        {isOwnerRevealEnabled && (
          <MenuItem href={'/owner-reveal'} id={COMMANDAI_IDS_MAP.chromeBar['owner-reveal']}>
            <HouseUser />
            Owner Reveal
          </MenuItem>
        )}
        <MenuItem href={'/map-builder'} id={COMMANDAI_IDS_MAP.chromeBar['map-builder']}>
          <MapMarkedAlt />
          Map Builder
        </MenuItem>
      </FlexColumn>
      <BottomSection>
        <MenuItem href={'/help-center'} id={COMMANDAI_IDS_MAP.chromeBar['help-center']}>
          <HelpOutline />
          Help Center
        </MenuItem>
        <MenuItem
          id={COMMANDAI_IDS_MAP.chromeBar['support']}
          href="" // or href="#" would also work
          disableactive={1}
          onClick={(e) => {
            e.preventDefault()
            window.CommandBar.toggleHelpHub()
          }}>
          <ChatDotsFill />
          Support
        </MenuItem>
        <PaddedBox padding={Shapes.spacing[4]}>
          <FlexColumn gap={Shapes.spacing[2]}>
            <ToolbarAdmin />
            <CommandAILink />
            <AtlasLink />
            <NavMenuModalToogle />
          </FlexColumn>
        </PaddedBox>
      </BottomSection>
    </Wrapper>
  )
}
